import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { Wrench, Users, Award, Shield, Search } from 'lucide-react';
import Footer from '../components/Footer';
import RecentlyAddedBusinesses from '../components/RecentlyAddedBusinesses';
import TraderScrollStrip from '../components/TraderScrollStrip';
import { supabase } from '../config/supabase';

export default function Home() {
  const [searchTerm, setSearchTerm] = useState('');
  const [location] = useState('');
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // Rotating text state
  const [rotatingWord, setRotatingWord] = useState('PROFESSIONALS');
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const words = ['PROFESSIONALS', 'SERVICES', 'PRODUCTS'];
    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade(false); // Start fade out
      setTimeout(() => {
        currentIndex = (currentIndex + 1) % words.length;
        setRotatingWord(words[currentIndex]);
        setFade(true); // Start fade in
      }, 500); // Wait for fade out to complete
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Get user session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setSessionLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchTerm)}&location=${encodeURIComponent(location)}`);
  };

  return (
    <>
      <div className="min-h-screen overflow-x-hidden pt-16">

        {/* Hero Section */}
        <div
          className="flex justify-center items-center relative bg-gradient-to-br from-blue-700 to-[#f0e4be] min-h-[600px] z-[10] bg-none md:bg-[url('/49.-Soft-Peach_1.jpg')] bg-right md:bg-right-center md:bg-no-repeat md:bg-auto"
          style={{
            backgroundAttachment: 'fixed'
          }}
        >
          {/* Overlay Image */}
          <div
            className="absolute inset-0 bg-center bg-no-repeat opacity-100 hidden md:block bg-right md:bg-none"
            style={{
              backgroundImage: 'url(/baker-construction.png)',
              backgroundSize: 'contain'
            }}
          />

          {/* Image above hero section for mobile */}
          <img
            src="/baker-construction.png"
            alt="Local Traders"
            className="w-full md:hidden absolute top-[2vh] left-0 z-10 object-cover"
            style={{
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)'
            }}
          />

          <div className="container mx-auto px-4 relative z-10 py-4 md:py-16 mt-[10vh] md:mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 items-center py-4 max-w-7xl mx-auto">
              {/* Left Column - Text Content */}
              <div className="hidden md:block">
                {/* Optional: Add a subtle background glow */}
                <div className="bg-cover bg-center bg-no-repeat  text-blue-900 
                  bg-transparent items-center
                  p-4 md:p-8 
                  relative 
                  overflow-hidden
                  bg-opacity-10 md:bg-opacity-10
                  justify-self-center iems-center my-4"
                >
                  <h1 className="hidden md:block text-2xl mb-6 text-center text-blue-900"
                    style={{
                      textShadow: '-1px -1px 0 #e5e7eb, 1px -1px 0 #e5e7eb, -1px 1px 0 #e5e7eb, 1px 1px 0 #e5e7eb'
                    }}>
                    LET PEOPLE KNOW about Your Services
                  </h1>
                  <p className="text-4xl mb-6 text-center font-bold text-blue-900 w-full md:w-full mx-auto"
                    style={{
                      textShadow: '-1px -1px 0 #e5e7eb, 1px -1px 0 #e5e7eb, -1px 1px 0 #e5e7eb, 1px 1px 0 #e5e7eb'
                    }}>
                    REGISTER YOUR BUSINESS <br />
                    <div
                      className="text-5xl text-blue-900 relative block w-full xl:w-[25vw] max-w-full aspect-[940/490] flex items-center justify-center bg-center bg-no-repeat bg-contain mx-auto"
                      style={{
                        backgroundImage: 'url(/zakreslenie.png)',
                        transform: 'rotate(-5deg)'
                      }}
                    >for FREE</div>
                  </p>
                </div>
                <div className="flex justify-center items-center">
                  <Link
                    to="/register/trader"
                    className="flex items-center justify-center w-full max-w-xs px-4 py-3 rounded-lg bg-orange-500 text-white 
                      shadow-lg shadow-orange-500/50 
                      hover:shadow-xl hover:shadow-orange-600/60
                      transform transition-all duration-300 
                      hover:-translate-y-1 
                      active:translate-y-0.5
                      active:shadow-md
                      text-base sm:text-xl font-semibold
                      border-b-4 border-orange-700
                      hover:bg-orange-600"
                  >
                    <Wrench className="w-5 h-5 mr-2" />
                    Register NOW !
                  </Link>
                </div>
                <div className="mt-4 hidden md:flex justify-end">
                  <img
                    src="/arrow-up-left.png"
                    alt="Arrow"
                    className="h-[30vh] w-auto"
                  />
                </div>
              </div>


              {/* Right Column - Search Form */}
              <div className="w-full max-w-md md:max-w-lg mx-auto space-y-8 md:space-y-4 justify-self-center mt-20">

                <form onSubmit={handleSearch} className="bg-white/15 backdrop-blur-md border border-white/80 
                  shadow-2xl 
                  rounded-2xl 
                  p-8 
                  relative 
                  overflow-hidden"
                  style={{
                    backgroundImage: '',
                    backgroundSize: 'cover',
                    backgroundPosition: 'left center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="flex flex-col gap-4 ">
                    <p className="text-3xl md:text-4xl mb-8 text-center font-bold ">
                      Find Local <br />
                      <span
                        className={`text-4xl md:text-5xl text-blue-900 transition-all duration-500 ease-in-out inline-block mx-auto text-center ${fade ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}
                        style={{
                          textShadow: '-1px -1px 0 #e5e7eb, 1px -1px 0 #e5e7eb, -1px 1px 0 #e5e7eb, 1px 1px 0 #e5e7eb'
                        }}
                      >
                        {rotatingWord}
                      </span>
                    </p>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-blue-800" />
                      </div>
                      <input
                        type="text"
                        placeholder="What are you looking for?"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 text-base border border-[#8cb3ff] rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent bg-white/70 text-black placeholder-gray-400"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#1e3a8a] hover:bg-[#1e40af] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-colors"
                    >
                      Search
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrolling strip of local businesses (traders, crafties, hospitality) */}
      <div className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
              Local Businesses Spotlight
            </h2>
          </div>
          <TraderScrollStrip
            limit={12}
            mode="latest"
            buttonText="View Details"
            colorScheme="light"
          />
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Join the Ballyconnell Local Directory</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Support your local community — discover, connect, and grow together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/trades" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <Shield className="w-12 h-12 text-blue-600 mb-6" />
              <h3 className="text-xl font-semibold mb-4">Made for Locals</h3>
              <p className="text-gray-600">
                Built to promote Ballyconnell&apos;s talent, services, makers, and businesses — all in one place.
              </p>
            </Link>

            <Link to="/trades" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <Users className="w-12 h-12 text-blue-600 mb-6" />
              <h3 className="text-xl font-semibold mb-4">🆓 Free to Join</h3>
              <p className="text-gray-600">
                Create a listing in minutes — no fees, no hassle. It&apos;s open to all local businesses and individuals.
              </p>
            </Link>

            <Link to="/trades" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <Award className="w-12 h-12 text-blue-600 mb-6" />
              <h3 className="text-xl font-semibold mb-4">Visible on Ballyconnell.org</h3>
              <p className="text-gray-600">
                Your listing will be visible on Ballyconnell.org, making it easy for locals to find you.
              </p>
            </Link>
          </div>
        </div>
      </div>

      {/* Recently Added Businesses Section */}
      <RecentlyAddedBusinesses />

      {/* CTA Sections */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 rounded-xl text-white">
              <Wrench className="w-12 h-12 mb-6" />
              <h3 className="text-2xl font-bold mb-4">Are you a skilled trader?</h3>
              <p className="mb-6">
                Join our network of professionals and grow your business
              </p>
              <Link
                to="/register/trader"
                className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Register as Trader
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}